/**
 * Shared axios-error → user-readable string extractor.
 *
 * Phase 1 QA revealed several failures where the UI showed "Failed to
 * create" or "Invalid input" when the backend had sent a much more
 * useful error. This normalizes the extraction:
 *
 *   1. zod validation details (fieldErrors)        -> "field: message"
 *   2. backend explicit human-readable message      -> use it
 *   3. backend error code (no message alongside)    -> use it
 *   4. axios generic message (e.g. timeout)         -> use it
 *   5. "Request failed" catch-all                   -> use the fallback arg
 *
 * Backend convention (see e.g. routes/candidates.ts `no_job_link`,
 * `NO_SUBMISSION`, esign.ts's signing-link errors): when a response carries
 * both fields, `error` is a short machine code for programmatic branching
 * and `message` is the full human sentence meant for display. `message` is
 * therefore checked first — checking `error` first showed raw codes like
 * "no_job_link" in toasts instead of the sentence the backend wrote.
 *
 * Use this in every catch block on a mutation path.
 */
type AxiosLikeError = {
  response?: {
    status?: number;
    data?: {
      error?: string;
      message?: string;
      details?: {
        fieldErrors?: Record<string, string[]>;
        formErrors?: string[];
      };
    };
  };
  message?: string;
  code?: string;
};

export function extractApiError(err: unknown, fallback = 'Request failed'): string {
  const e = err as AxiosLikeError;

  // Axios-timeout specifically — surface as something more actionable
  if (e.code === 'ECONNABORTED') return 'Request timed out. Please retry.';

  // Zod validation details → "field: first message"
  const fieldErrors = e.response?.data?.details?.fieldErrors;
  if (fieldErrors) {
    const firstKey = Object.keys(fieldErrors)[0];
    const firstMsg = firstKey ? fieldErrors[firstKey]?.[0] : undefined;
    if (firstKey && firstMsg) return `${firstKey}: ${firstMsg}`;
  }

  // formErrors array from zod (top-level refines)
  const formErrors = e.response?.data?.details?.formErrors;
  if (formErrors?.length) return formErrors[0];

  // backend explicit message/error — message (the human sentence) wins
  if (e.response?.data?.message) return e.response.data.message;
  if (e.response?.data?.error) return e.response.data.error;

  if (e.message) return e.message;
  return fallback;
}
